import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useCart } from "@/context/CartContext";
import SEO from "@/components/SEO";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paypalOrderId = params.get("paypal_order_id");
  const [status, setStatus] = useState("loading");
  const [orderNumber, setOrderNumber] = useState(null);
  const { clear } = useCart();

  const check = useCallback(async () => {
    if (paypalOrderId) {
      try {
        const r = await api.post(`/paypal/capture/${paypalOrderId}`);
        if (r.data.status === "paid") {
          setStatus("paid");
          setOrderNumber(r.data.order_number);
          clear();
        } else {
          setStatus("pending");
        }
      } catch (error) {
        logger.error("PayPal capture failed:", error);
        setStatus("failed");
      }
      return;
    }
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    // Poll for up to 20s
    for (let i = 0; i < 10; i += 1) {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          setOrderNumber(r.data.order_number);
          clear();
          return;
        }
      } catch (error) {
        logger.warn("Payment status poll failed, retrying:", error);
      }
      // Retry the polling loop after a short delay.
      await new Promise((res) => setTimeout(res, 2000));
    }
    setStatus("pending");
  }, [paypalOrderId, sessionId, clear]);

  useEffect(() => {
    check();
  }, [check]);

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-24 text-center fade-in" data-testid="page-payment-success">
      <SEO title="Order confirmation" path="/payment/success" />
      {status === "loading" && (
        <>
          <Loader2 size={40} className="mx-auto animate-spin text-[#0066FF]" />
          <h1 className="text-h2 mt-6">Confirming your payment...</h1>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={56} className="mx-auto text-[#34C759]" />
          <h1 className="text-h2 mt-6">Order confirmed.</h1>
          <p className="text-[#86868B] mt-3">Thanks for shopping with CreatorMic Pro. A confirmation email is on its way.</p>
          {orderNumber && <p className="mt-6 font-mono-tech text-sm text-[#1D1D1F]" data-testid="success-order-number">Order #{orderNumber}</p>}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/track" className="btn-secondary">Track order</Link>
            <Link to="/shop" className="btn-primary">Continue shopping</Link>
          </div>
        </>
      )}
      {status === "pending" && (
        <>
          <Loader2 size={40} className="mx-auto animate-spin text-[#0066FF]" />
          <h1 className="text-h2 mt-6">Payment processing</h1>
          <p className="text-[#86868B] mt-3">Your payment is still being verified. We will email you when it clears.</p>
        </>
      )}
      {status === "failed" && (
        <>
          <XCircle size={56} className="mx-auto text-[#FF3B30]" />
          <h1 className="text-h2 mt-6">Something went wrong</h1>
          <p className="text-[#86868B] mt-3">We could not confirm your payment. Please try again or contact support.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/checkout" className="btn-primary">Try again</Link>
            <Link to="/contact" className="btn-secondary">Contact us</Link>
          </div>
        </>
      )}
    </div>
  );
}
